import React, { useState, useRef } from 'react';
import { SignatureField, TaxDocument } from '../types';
import { updateDocumentSignatureFields, signDocumentField, getClientIp } from '../services/firestoreService';
import { useAuth } from '../context/AuthContext';
import { Timestamp } from 'firebase/firestore';

interface SignaturePortalProps {
  caseId: string;
  document: TaxDocument;
  isStaff: boolean;
  onClose: () => void;
}

const SignaturePortal: React.FC<SignaturePortalProps> = ({ caseId, document, isStaff, onClose }) => {
  // Fix: staffFirmId replaced with firmId from AuthContext.
  const { firmId: contextFirmId, userDoc } = useAuth();
  const [fields, setFields] = useState<SignatureField[]>(document.signatureFields || []);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resolve firm ID
  const firmId = contextFirmId || userDoc?.firmId;

  const handleContainerClick = (e: React.MouseEvent) => {
    if (!isStaff) return;
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newField: SignatureField = {
      id: `sig-${Date.now()}`,
      x,
      y,
      signed: false
    };

    setFields([...fields, newField]);
  };

  const removeField = (id: string) => {
    if (!isStaff) return;
    setFields(fields.filter(f => f.id !== id));
  };

  const saveSignatureRequests = async () => {
    if (!firmId) return;
    setIsSaving(true);
    try {
      await updateDocumentSignatureFields(firmId, caseId, document.id, fields);
      alert("Signature fields placed and client notified.");
      onClose();
    } catch (e) {
      alert("Error saving signature fields.");
    } finally {
      setIsSaving(false);
    }
  };

  const executeSignature = async (fieldId: string) => {
    if (isStaff || !firmId) return;
    if (!window.confirm("Click 'OK' to electronically sign this document. This will record your current IP address and timestamp as a legal audit trail.")) return;
    
    setIsSaving(true);
    try {
      const ip = await getClientIp();
      await signDocumentField(firmId, caseId, document.id, fieldId, ip);
      
      // Local update for UI responsiveness
      setFields(fields.map(f => f.id === fieldId ? { 
        ...f, 
        signed: true, 
        signedAt: Timestamp.now(),
        ipAddress: ip
      } : f));
    } catch (e) {
      alert("Error signing document.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-full flex flex-col overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <span>✍️</span> {isStaff ? 'Prepare Signature Requests' : 'Sign Document'}
            </h2>
            <p className="text-xs text-slate-500">{document.fileName}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-200 p-8 flex justify-center items-start">
          <div 
            ref={containerRef}
            onClick={handleContainerClick}
            className={`relative bg-white shadow-lg origin-top transition-transform ${isStaff ? 'cursor-crosshair' : ''}`}
            style={{ width: '800px', minHeight: '1100px' }}
          >
            {/* Visual Doc Simulation */}
            <div className="p-12 space-y-6 select-none opacity-40">
               <div className="h-8 bg-slate-200 w-1/3 rounded"></div>
               <div className="h-4 bg-slate-100 w-full rounded"></div>
               <div className="h-4 bg-slate-100 w-full rounded"></div>
               <div className="h-4 bg-slate-100 w-3/4 rounded"></div>
               <div className="grid grid-cols-2 gap-8 mt-12">
                  <div className="space-y-4">
                    <div className="h-4 bg-slate-100 w-full rounded"></div>
                    <div className="h-4 bg-slate-100 w-full rounded"></div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-slate-100 w-full rounded"></div>
                    <div className="h-4 bg-slate-100 w-full rounded"></div>
                  </div>
               </div>
               <div className="h-64 bg-slate-50 w-full rounded border-2 border-slate-100 flex items-center justify-center text-slate-300 font-bold uppercase tracking-widest">
                  Secure Document Preview
               </div>
            </div>

            {/* Signature Fields Layer */}
            {fields.map((field) => (
              <div 
                key={field.id}
                className="absolute z-10"
                style={{ left: `${field.x}%`, top: `${field.y}%`, transform: 'translate(-50%, -50%)' }}
                onClick={(e) => e.stopPropagation()}
              >
                {field.signed ? (
                  <div className="bg-emerald-50 border-2 border-emerald-500 rounded-md p-2 flex flex-col items-center shadow-md animate-bounce-short min-w-[160px]">
                    <span className="text-emerald-600 font-serif italic text-lg leading-none">Digitally Signed</span>
                    <div className="flex flex-col items-center mt-1.5 border-t border-emerald-100 pt-1 w-full">
                       <span className="text-[7px] text-emerald-500 font-black uppercase tracking-tighter">
                          Verified: {field.signedAt?.toDate ? field.signedAt.toDate().toLocaleString() : new Date().toLocaleString()}
                       </span>
                       <span className="text-[7px] text-emerald-500 font-black uppercase tracking-tighter">
                          IP: {field.ipAddress || 'Verified'}
                       </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 group">
                    <button
                      onClick={() => !isStaff && executeSignature(field.id)}
                      className={`px-4 py-2 rounded border-2 flex items-center gap-2 shadow-lg transition-all ${
                        isStaff 
                        ? 'bg-brand-50 border-brand-500 text-brand-700 cursor-default' 
                        : 'bg-brand-600 border-brand-600 text-white hover:bg-brand-700 hover:scale-110 active:scale-95'
                      }`}
                    >
                      <span className="text-lg">✍️</span>
                      <span className="text-xs font-bold uppercase tracking-wider">{isStaff ? 'Signature Request' : 'Click to Sign'}</span>
                    </button>
                    {isStaff && (
                       <button 
                        onClick={() => removeField(field.id)}
                        className="bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                       >
                         <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                       </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-white">
          <div className="text-xs text-slate-500 italic">
            {isStaff 
              ? "Click anywhere on the document to place a signature field." 
              : `Pending Signatures: ${fields.filter(f => !f.signed).length}`
            }
          </div>
          <div className="flex gap-3">
             <button 
               onClick={onClose}
               className="px-6 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
             >
               Cancel
             </button>
             {isStaff && (
                <button
                  onClick={saveSignatureRequests}
                  disabled={isSaving || fields.length === 0}
                  className="bg-brand-600 text-white px-8 py-2 rounded-lg font-bold hover:bg-brand-700 shadow-md transition-all disabled:opacity-50"
                >
                  {isSaving ? 'Requesting...' : 'Confirm Signature Requests'}
                </button>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePortal;
